import { createProduct } from './products';
import { supabase } from './client';

const MISSING_COLUMN_REGEX = /column "([^"]+)" of relation "collaborative_product_pool" does not exist/i;

const formatPoolError = (error: any): Error => {
  const code = error?.code || '';
  const message = error?.message || 'Unknown collaborative product pool error';

  if (code === '23514') {
    return new Error('Valor invalido para una constraint del pool (por ejemplo groceries_subcategory). Revisa categorias permitidas y vuelve a intentar.');
  }

  if (code === '23505') {
    return new Error('Ya existe un producto en el pool con ese barcode o EAD.');
  }

  return new Error(message);
};

const insertPoolWithSchemaFallback = async (inputPayload: Record<string, any>) => {
  const payload = { ...inputPayload };

  for (let attempt = 0; attempt < 12; attempt += 1) {
    const { data, error } = await supabase
      .from('collaborative_product_pool')
      .insert(payload)
      .select(SELECT_FIELDS)
      .single();

    if (!error) return data;

    const missingColumnMatch = String(error?.message || '').match(MISSING_COLUMN_REGEX);
    if (missingColumnMatch?.[1]) {
      const missingColumn = missingColumnMatch[1];
      if (Object.prototype.hasOwnProperty.call(payload, missingColumn)) {
        delete payload[missingColumn];
        continue;
      }
    }

    throw formatPoolError(error);
  }

  throw new Error('No se pudo guardar en el pool: demasiadas columnas incompatibles con el esquema actual. Aplica migraciones pendientes.');
};

const updatePoolWithSchemaFallback = async (itemId: string, inputPayload: Record<string, any>) => {
  const payload = { ...inputPayload };

  for (let attempt = 0; attempt < 12; attempt += 1) {
    const { data, error } = await supabase
      .from('collaborative_product_pool')
      .update(payload)
      .eq('id', itemId)
      .select(SELECT_FIELDS)
      .single();

    if (!error) return data;

    const missingColumnMatch = String(error?.message || '').match(MISSING_COLUMN_REGEX);
    if (missingColumnMatch?.[1]) {
      const missingColumn = missingColumnMatch[1];
      if (Object.prototype.hasOwnProperty.call(payload, missingColumn)) {
        delete payload[missingColumn];
        continue;
      }
    }

    throw formatPoolError(error);
  }

  throw new Error('No se pudo actualizar en el pool: demasiadas columnas incompatibles con el esquema actual. Aplica migraciones pendientes.');
};

export interface CollaborativeProductPoolItem {
  id: string;
  barcode: string | null;
  ead: string | null;
  title: string;
  description: string | null;
  quantity: number;
  image_url: string | null;
  fixed_rental_price: number | null;
  fixed_sale_price: number | null;
  free_shipping: boolean;
  condition: string | null;
  image_urls: string[];
  main_image_url: string | null;
  details: Record<string, any> | null;
  location_name: string | null;
  latitude: number | null;
  longitude: number | null;
  rental_options: Array<{ days: number; price: number }> | null;
  brand: string | null;
  model: string | null;
  content: string | null;
  stock: number | null;
  weight: number | null;
  is_digital: boolean | null;
  departure_time: string | null;
  timezone: string | null;
  over18: boolean;
  nightshift: boolean;
  replicable: boolean;
  groceries_subcategory: string | null;
  product_type: string;
  created_by: string | null;
  updated_by: string | null;
  last_cloned_at: string | null;
  last_cloned_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface UpsertCollaborativeProductPoolPayload {
  barcode?: string | null;
  ead?: string | null;
  title: string;
  description?: string | null;
  quantity?: number;
  image_url?: string | null;
  fixed_rental_price?: number | null;
  fixed_sale_price?: number | null;
  free_shipping?: boolean;
  condition?: string | null;
  image_urls?: string[];
  main_image_url?: string | null;
  details?: Record<string, any> | null;
  location_name?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  rental_options?: Array<{ days: number; price: number }> | null;
  brand?: string | null;
  model?: string | null;
  content?: string | null;
  stock?: number | null;
  weight?: number | null;
  is_digital?: boolean | null;
  departure_time?: string | null;
  timezone?: string | null;
  over18?: boolean;
  nightshift?: boolean;
  replicable?: boolean;
  groceries_subcategory?: string | null;
  product_type?: string;
}

const SELECT_FIELDS =
  'id,barcode,ead,title,description,quantity,image_url,fixed_rental_price,fixed_sale_price,free_shipping,condition,image_urls,main_image_url,details,location_name,latitude,longitude,rental_options,brand,model,content,stock,weight,is_digital,departure_time,timezone,over18,nightshift,replicable,groceries_subcategory,product_type,created_by,updated_by,last_cloned_at,last_cloned_by,created_at,updated_at';

export const getCollaborativeProductPool = async (
  searchTerm: string | null = null,
  options?: {
    product_type?: string;
    groceries_subcategory?: string;
    limit?: number;
    offset?: number;
  }
): Promise<CollaborativeProductPoolItem[]> => {
  let query = supabase
    .from('collaborative_product_pool')
    .select(SELECT_FIELDS)
    .order('updated_at', { ascending: false });

  const normalized = (searchTerm || '').trim();
  if (normalized) {
    const term = `%${normalized}%`;
    query = query.or(`title.ilike.${term},barcode.ilike.${term},ead.ilike.${term}`);
  }

  if (options?.product_type) {
    query = query.eq('product_type', options.product_type);
  }
  if (options?.groceries_subcategory) {
    query = query.eq('groceries_subcategory', options.groceries_subcategory);
  }
  if (typeof options?.limit === 'number') {
    query = query.limit(options.limit);
  }
  if (typeof options?.offset === 'number') {
    query = query.offset(options.offset);
  }

  const { data, error } = await query;
  if (error) {
    console.error('Error loading collaborative product pool:', error);
    throw error;
  }

  return (data || []) as CollaborativeProductPoolItem[];
};

export const createCollaborativeProductPoolItem = async (
  payload: UpsertCollaborativeProductPoolPayload,
  userId: string
): Promise<CollaborativeProductPoolItem> => {
  const normalizedQuantity = Number.isFinite(payload.quantity) ? Number(payload.quantity) : 1;
  const normalizedStock = Number.isFinite(payload.stock) ? Math.max(0, Math.trunc(Number(payload.stock))) : Math.max(0, Math.trunc(normalizedQuantity));

  const insertPayload = {
    barcode: payload.barcode || null,
    ead: payload.ead || null,
    title: payload.title.trim(),
    description: payload.description || null,
    quantity: normalizedQuantity,
    image_url: payload.image_url || null,
    fixed_rental_price: Number.isFinite(payload.fixed_rental_price) ? Number(payload.fixed_rental_price) : null,
    fixed_sale_price: Number.isFinite(payload.fixed_sale_price) ? Number(payload.fixed_sale_price) : null,
    free_shipping: Boolean(payload.free_shipping),
    condition: payload.condition || null,
    image_urls: Array.isArray(payload.image_urls) ? payload.image_urls : [],
    main_image_url: payload.main_image_url || payload.image_url || null,
    details: payload.details || {},
    location_name: payload.location_name || null,
    latitude: Number.isFinite(payload.latitude) ? Number(payload.latitude) : null,
    longitude: Number.isFinite(payload.longitude) ? Number(payload.longitude) : null,
    rental_options: Array.isArray(payload.rental_options) ? payload.rental_options : [],
    brand: payload.brand || null,
    model: payload.model || null,
    content: payload.content || null,
    stock: normalizedStock,
    weight: Number.isFinite(payload.weight) ? Number(payload.weight) : null,
    is_digital: typeof payload.is_digital === 'boolean' ? payload.is_digital : null,
    departure_time: payload.departure_time || null,
    timezone: payload.timezone || 'America/Argentina/Buenos_Aires',
    over18: Boolean(payload.over18),
    nightshift: Boolean(payload.nightshift),
    replicable: payload.replicable ?? true,
    groceries_subcategory: payload.groceries_subcategory || null,
    product_type: payload.product_type || 'groceries',
    created_by: userId,
    updated_by: userId,
  };

  try {
    const data = await insertPoolWithSchemaFallback(insertPayload);
    return data as CollaborativeProductPoolItem;
  } catch (error) {
    console.error('Error creating collaborative pool item:', error);
    throw error;
  }
};

export const updateCollaborativeProductPoolItem = async (
  itemId: string,
  payload: UpsertCollaborativeProductPoolPayload,
  userId: string
): Promise<CollaborativeProductPoolItem> => {
  const normalizedQuantity = Number.isFinite(payload.quantity) ? Number(payload.quantity) : 1;
  const normalizedStock = Number.isFinite(payload.stock) ? Math.max(0, Math.trunc(Number(payload.stock))) : Math.max(0, Math.trunc(normalizedQuantity));

  const updatePayload = {
    barcode: payload.barcode || null,
    ead: payload.ead || null,
    title: payload.title.trim(),
    description: payload.description || null,
    quantity: normalizedQuantity,
    image_url: payload.image_url || null,
    fixed_rental_price: Number.isFinite(payload.fixed_rental_price) ? Number(payload.fixed_rental_price) : null,
    fixed_sale_price: Number.isFinite(payload.fixed_sale_price) ? Number(payload.fixed_sale_price) : null,
    free_shipping: Boolean(payload.free_shipping),
    condition: payload.condition || null,
    image_urls: Array.isArray(payload.image_urls) ? payload.image_urls : [],
    main_image_url: payload.main_image_url || payload.image_url || null,
    details: payload.details || {},
    location_name: payload.location_name || null,
    latitude: Number.isFinite(payload.latitude) ? Number(payload.latitude) : null,
    longitude: Number.isFinite(payload.longitude) ? Number(payload.longitude) : null,
    rental_options: Array.isArray(payload.rental_options) ? payload.rental_options : [],
    brand: payload.brand || null,
    model: payload.model || null,
    content: payload.content || null,
    stock: normalizedStock,
    weight: Number.isFinite(payload.weight) ? Number(payload.weight) : null,
    is_digital: typeof payload.is_digital === 'boolean' ? payload.is_digital : null,
    departure_time: payload.departure_time || null,
    timezone: payload.timezone || 'America/Argentina/Buenos_Aires',
    over18: Boolean(payload.over18),
    nightshift: Boolean(payload.nightshift),
    replicable: payload.replicable ?? true,
    groceries_subcategory: payload.groceries_subcategory || null,
    product_type: payload.product_type || 'groceries',
    updated_by: userId,
    updated_at: new Date().toISOString(),
  };

  try {
    const data = await updatePoolWithSchemaFallback(itemId, updatePayload);
    return data as CollaborativeProductPoolItem;
  } catch (error) {
    console.error('Error updating collaborative pool item:', error);
    throw error;
  }
};

export const upsertCollaborativeProductPoolItemByCode = async (
  payload: UpsertCollaborativeProductPoolPayload,
  userId: string
): Promise<CollaborativeProductPoolItem> => {
  const barcode = payload.barcode ? String(payload.barcode).trim() : null;
  const ead = payload.ead ? String(payload.ead).trim() : null;

  let existingItem: CollaborativeProductPoolItem | null = null;

  if (barcode) {
    const { data, error } = await supabase
      .from('collaborative_product_pool')
      .select(SELECT_FIELDS)
      .eq('barcode', barcode)
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('Error finding collaborative pool item by barcode:', error);
      throw error;
    }

    existingItem = (data as CollaborativeProductPoolItem | null) || null;
  }

  if (!existingItem && ead) {
    const { data, error } = await supabase
      .from('collaborative_product_pool')
      .select(SELECT_FIELDS)
      .eq('ead', ead)
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('Error finding collaborative pool item by EAD:', error);
      throw error;
    }

    existingItem = (data as CollaborativeProductPoolItem | null) || null;
  }

  if (existingItem?.id) {
    return updateCollaborativeProductPoolItem(existingItem.id, payload, userId);
  }

  return createCollaborativeProductPoolItem(payload, userId);
};

export const cloneCollaborativePoolItemToStore = async (
  item: CollaborativeProductPoolItem,
  sellerId: string,
  storeId: string | null,
  overrides?: {
    stock?: number | null;
    fixed_sale_price?: number | null;
  }
) => {
  const safeProductType = item.product_type || 'groceries';
  const imageUrls = Array.isArray(item.image_urls) && item.image_urls.length > 0
    ? item.image_urls.filter(Boolean)
    : item.image_url
      ? [item.image_url]
      : [];
  const mainImageUrl = item.main_image_url || item.image_url || imageUrls[0] || null;

  const details: Record<string, any> = {
    collaborative_pool_product_id: item.id,
    collaborative_pool: true,
    barcode: item.barcode || null,
    ead: item.ead || null,
    groceries_subcategory: item.groceries_subcategory || null,
    ...(item.details || {}),
  };

  const overrideSalePrice = Number.isFinite(overrides?.fixed_sale_price)
    ? Number(overrides?.fixed_sale_price)
    : null;

  const effectiveSalePrice = overrideSalePrice ?? (Number.isFinite(item.fixed_sale_price) ? Number(item.fixed_sale_price) : null);


  // Checar si ya existe un producto con el mismo barcode en el destino (tienda o perfil personal)
  if (item.barcode) {
    let existing: { id: string } | null = null;
    let checkError: any = null;

    if (storeId) {
      const result = await supabase
        .from('products')
        .select('id')
        .eq('store_id', storeId)
        .eq('barcode', item.barcode)
        .maybeSingle();
      existing = result.data as { id: string } | null;
      checkError = result.error;
    } else {
      const result = await supabase
        .from('products')
        .select('id')
        .eq('seller_id', sellerId)
        .is('store_id', null)
        .eq('barcode', item.barcode)
        .maybeSingle();
      existing = result.data as { id: string } | null;
      checkError = result.error;
    }

    if (checkError) {
      throw new Error('Error verificando unicidad de código de barras en la tienda.');
    }
    if (existing && existing.id) {
      throw new Error(
        storeId
          ? 'Ya tienes un producto con ese código de barras en tu tienda. No se puede clonar duplicado.'
          : 'Ya tienes un producto con ese código de barras en tu perfil personal. No se puede clonar duplicado.'
      );
    }
  }

  const createdProduct = await createProduct(
    sellerId,
    item.title,
    null,
    item.description || null,
    mainImageUrl,
    Number.isFinite(item.fixed_rental_price) ? Number(item.fixed_rental_price) : null,
    effectiveSalePrice,
    Boolean(item.free_shipping),
    false,
    null,
    null,
    null,
    null,
    safeProductType,
    item.condition || null,
    imageUrls,
    mainImageUrl,
    'available',
    details,
    Array.isArray(item.rental_options) ? item.rental_options : null,
    Number.isFinite(item.latitude) ? Number(item.latitude) : null,
    Number.isFinite(item.longitude) ? Number(item.longitude) : null,
    item.location_name || null,
    storeId
  );

  const normalizedStock = Number.isFinite(item.stock)
    ? Math.max(0, Math.trunc(Number(item.stock)))
    : Math.max(0, Math.trunc(Number(item.quantity || 0)));

  const overrideStock = Number.isFinite(overrides?.stock)
    ? Math.max(0, Math.trunc(Number(overrides?.stock)))
    : null;

  const effectiveStock = overrideStock ?? normalizedStock;

  await supabase
    .from('products')
    .update({
      stock: effectiveStock,
      fixed_sale_price: effectiveSalePrice,
      over18: Boolean(item.over18),
      nightshift: Boolean(item.nightshift),
      weight: Number.isFinite(item.weight) ? Number(item.weight) : null,
      is_digital: typeof item.is_digital === 'boolean' ? item.is_digital : null,
      departure_time: item.departure_time || null,
      timezone: item.timezone || 'America/Argentina/Buenos_Aires',
      replicable: item.replicable ?? true,
      groceries_subcategory: item.groceries_subcategory || null,
      barcode: item.barcode || null,
      brand: item.brand || null,
      model: item.model || null,
      content: item.content || null,
      image_urls: imageUrls,
      main_image_url: mainImageUrl,
      details,
    })
    .eq('id', createdProduct.id);

  await supabase
    .from('collaborative_product_pool')
    .update({
      last_cloned_at: new Date().toISOString(),
      last_cloned_by: sellerId,
    })
    .eq('id', item.id);

  return createdProduct;
};
